import { Injectable } from '@angular/core';
import { GetTimestampFromTime } from '@dave/types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { exhaustMap, firstValueFrom } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { isNotNullOrUndefined } from '../../../helper/helper';
import { TravelZoneEntity, TravelZoneEntityFromBackend } from '../../entities/travel-zone.entity';
import { AppGatewayService } from '../../services/app-gateway.service';
import { DaveActions } from '../actions/actions';
import { TravelZoneActionTypes } from '../actions/travel-zone.actions';
import { State } from '../index';
import { getTravelZoneDictionary } from '../selectors/travel-zone.selector';
import { BaseActionTypes } from '../actions/base.actions';
import { TravelZoneGetRequest } from '@dave/types/dist/proto/commission/travelZone';

const ErrorCodes = {
    Add: TravelZoneEntity.EntityName + ' Hinzufügen fehlgeschlagen',
    Load: TravelZoneEntity.EntityName + ' Abrufen fehlgeschlagen',
    Modify: TravelZoneEntity.EntityName + ' Bearbeiten fehlgeschlagen',
};

@Injectable()
export class TravelZoneEffects {
    ModifyTravelZone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TravelZoneActionTypes.ChangeRequest),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ CommissionTravelZoneEdit: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return TravelZoneActionTypes.ChangeSuccess({ Payload: TravelZoneEntityFromBackend(res.CommissionTravelZoneEdit.travelZone) });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        TravelZoneActionTypes.ChangeFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Modify,
                                Errors: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    AddTravelZone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TravelZoneActionTypes.AddRequest),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ CommissionTravelZoneAdd: Payload })
                    .then((res) => {
                        console.log(res)
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return TravelZoneActionTypes.AddSuccess({ Payload: TravelZoneEntityFromBackend(res.CommissionTravelZoneAdd.travelZone) });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        TravelZoneActionTypes.AddFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Errors: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    LoadTravelZones = createEffect(() => {
        return this.actions$.pipe(
            ofType(TravelZoneActionTypes.LoadRequest),
            exhaustMap(({ Payload }) => {
                const requestPayload: TravelZoneGetRequest = {
                    WithDeleted: true,
                    Ids: [],
                };
                if (isNotNullOrUndefined(Payload.updatedSince)) {
                    requestPayload.UpdatedSince = Payload.updatedSince;
                }

                return this.gatewayService
                    .Request({ CommissionTravelZoneGet: requestPayload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            if (!isNotNullOrUndefined(Payload.updatedSince)) {
                                return TravelZoneActionTypes.LoadSuccess({
                                    Payload: res.CommissionTravelZoneGet.travelZones.map((val) => TravelZoneEntityFromBackend(val)),
                                    updateLatestUpdatedAt: true,
                                });
                            } else {
                                return firstValueFrom(this.store$.select(getTravelZoneDictionary)).then((TravelZones) => {
                                    const filteredTravelZones = res.CommissionTravelZoneGet.travelZones.filter((TravelZone) => {
                                        const fromState = TravelZones[TravelZone.Id];
                                        return !fromState || GetTimestampFromTime(fromState.UpdatedAt).toString() !== TravelZone.UpdatedAt;
                                    });

                                    return TravelZoneActionTypes.LoadSuccess({
                                        Payload: filteredTravelZones.map((val) => TravelZoneEntityFromBackend(val)),
                                        updateLatestUpdatedAt: true,
                                    });
                                });
                            }
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        TravelZoneActionTypes.LoadFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Load,
                                Errors: err,
                            },
                        }),
                    );
            }),
        );
    });
    error$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TravelZoneActionTypes.LoadFailure, TravelZoneActionTypes.ChangeFailure, TravelZoneActionTypes.AddFailure),
            map(({ Payload }) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: Payload.ToasterMessage,
                        Err: Payload.Errors,
                    },
                }),
            ),
        ),
    );
    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private gatewayService: AppGatewayService) {}
}
